import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FC, memo, useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ConditionExpression,
  StepRequirement,
  TOnboardingConfig,
  TVerificationAppFieldConfigItem,
} from "../../models";
import { ConditionEditorDialog } from "./ConditionEditorDialog";
import { ConditionPreview } from "./ConditionPreview";
import {
  createSimpleCondition,
  formatConditionInline,
} from "./conditionHelpers";

const StepRequirementRow: FC<{
  availableFields: TVerificationAppFieldConfigItem[];
  defaultExpanded?: boolean;
  index: number;
  requirement: StepRequirement;
  onEditCondition: (index: number) => void;
  onMessageChange: (index: number, message: string) => void;
  onRemove: (index: number) => void;
}> = memo(({
  availableFields,
  defaultExpanded = false,
  index,
  requirement,
  onEditCondition,
  onMessageChange,
  onRemove,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);
  const stop = (event: React.MouseEvent) => event.stopPropagation();
  const title = requirement.message.trim() || "Requirement";
  const summary = useMemo(
    () => formatConditionInline(requirement.condition),
    [requirement.condition],
  );

  return (
    <Box>
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        onClick={toggle}
        sx={{
          py: 0.75,
          px: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <IconButton
          aria-label={expanded ? "Collapse requirement" : "Expand requirement"}
          size='small'
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
          onClick={(event) => {
            stop(event);
            toggle();
          }}
        >
          <ExpandMoreIcon fontSize='small' />
        </IconButton>

        <Stack
          direction='row'
          spacing={1.5}
          alignItems='center'
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: 500,
              color: requirement.message.trim() ? "text.primary" : "text.disabled",
              fontStyle: requirement.message.trim() ? undefined : "italic",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 0,
              maxWidth: 260,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              minWidth: 0,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "Menlo, Monaco, Consolas, monospace",
            }}
          >
            {summary}
          </Typography>
        </Stack>

        <IconButton
          aria-label='Delete requirement'
          onClick={(event) => {
            stop(event);
            onRemove(index);
          }}
          size='small'
          sx={{
            color: (theme) => alpha(theme.palette.error.main, 0.45),
            "&:hover": {
              color: "error.main",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            },
          }}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1.5} sx={{ pt: 1, pb: 2, pl: 4.5, maxWidth: 500 }}>
          <TextField
            label='Message'
            value={requirement.message}
            onChange={(event) => onMessageChange(index, event.target.value)}
            size='small'
            fullWidth
            required
          />
          <ConditionPreview
            availableFields={availableFields}
            condition={requirement.condition}
            onEdit={() => onEditCondition(index)}
            title='Continue when (require)'
          />
        </Stack>
      </Collapse>
    </Box>
  );
});

export const StepRequirementsEditor: FC<{
  availableFields: TVerificationAppFieldConfigItem[];
  config: TOnboardingConfig;
  requirements?: StepRequirement[];
  stepIndex: number;
  onChange: (requirements: StepRequirement[] | undefined) => void;
}> = memo(({
  availableFields,
  config,
  requirements,
  stepIndex,
  onChange,
}) => {
  const [editingRequirementIndex, setEditingRequirementIndex] = useState<number | null>(null);
  const requirementList = useMemo(() => requirements ?? [], [requirements]);
  const editingRequirement = editingRequirementIndex === null
    ? null
    : requirementList[editingRequirementIndex] ?? null;

  const commitRequirements = useCallback(
    (nextRequirements: StepRequirement[]) => {
      onChange(nextRequirements.length > 0 ? nextRequirements : undefined);
    },
    [onChange],
  );

  const handleAddRequirement = useCallback(() => {
    commitRequirements([
      ...requirementList,
      {
        message: "",
        condition: createSimpleCondition(availableFields),
      },
    ]);
  }, [availableFields, commitRequirements, requirementList]);

  const handleMessageChange = useCallback(
    (requirementIndex: number, message: string) => {
      commitRequirements(requirementList.map((requirement, index) => (
        index === requirementIndex ? { ...requirement, message } : requirement
      )));
    },
    [commitRequirements, requirementList],
  );

  const handleConditionChange = useCallback(
    (condition: ConditionExpression) => {
      if (editingRequirementIndex === null) {
        return;
      }

      commitRequirements(requirementList.map((requirement, index) => (
        index === editingRequirementIndex ? { ...requirement, condition } : requirement
      )));
      setEditingRequirementIndex(null);
    },
    [commitRequirements, editingRequirementIndex, requirementList],
  );

  const handleRemoveRequirement = useCallback(
    (requirementIndex: number) => {
      commitRequirements(requirementList.filter((_, index) => index !== requirementIndex));
    },
    [commitRequirements, requirementList],
  );

  return (
    <Stack spacing={1.25} sx={{ pt: 2 }}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          Requirements
        </Typography>
      </Stack>
      {requirementList.length === 0 ? (
        <Typography color='text.secondary' variant='body2'>
          No step requirements.
        </Typography>
      ) : (
        requirementList.map((requirement, requirementIndex) => (
          <StepRequirementRow
            key={requirementIndex}
            availableFields={availableFields}
            defaultExpanded={!requirement.message.trim()}
            index={requirementIndex}
            requirement={requirement}
            onEditCondition={setEditingRequirementIndex}
            onMessageChange={handleMessageChange}
            onRemove={handleRemoveRequirement}
          />
        ))
      )}
      <Button
        size='small'
        startIcon={<AddIcon />}
        onClick={handleAddRequirement}
        sx={{ alignSelf: "flex-start" }}
      >
        Add requirement
      </Button>
      {editingRequirement && editingRequirementIndex !== null ? (
        <ConditionEditorDialog
          availableFields={availableFields}
          condition={editingRequirement.condition}
          open
          stepIndex={stepIndex}
          title='Edit requirement condition'
          validationConfig={config}
          validationMode='stepRequirement'
          validationPath={`/elements/${stepIndex}/requirements/${editingRequirementIndex}/condition`}
          onClose={() => setEditingRequirementIndex(null)}
          onSave={handleConditionChange}
        />
      ) : null}
    </Stack>
  );
});
